import { CruisesContract, definitions, paths } from '@luxuryescapes/contract-svc-cruise';

import cruisesAPI from './CruisesApiService';

const resource = 'departures';

export type GetDeparturesParams = definitions['GET_DEPARTURES_QUERY'];
export type DepartureBase = definitions['DEPARTURE_BASE'];
export type Departure = definitions['DEPARTURE'];
export type DataFeed = definitions['DEPARTURE_DATA_FEED'];
export type DataFeedDiffs = definitions['DEPARTURE_DATA_FEED_DIFFS'];
export type UpdateDepartureBody =
  paths['/api/cruise/v2/departures/{departureId}']['put']['requestBody']['content']['application/json'];

const listWithPagination = async (
  paginationParams: GetDeparturesParams,
): Promise<CruisesContract.Response<Array<DepartureBase>>> => {
  return await cruisesAPI.http.get({ resource, paginationParams });
};

const getById = async (id: string): Promise<CruisesContract.Response<Departure>> => {
  let status: number;

  try {
    const res: CruisesContract.Response<Departure> = await cruisesAPI.http.get({
      resource: `${resource}/${id}`,
    });

    status = res.status;
    if (res.result && !res.errors) {
      return res;
    }
  } catch (err) {
    console.error('[Cruise Departure - Get By Id]: ', err);
    return { status, message: err, result: null };
  }
};

type GenericMessageResponse = CruisesContract.Response<CruisesContract.GenericMessageResponse>;

const updateById = async (id: string, data: UpdateDepartureBody): Promise<GenericMessageResponse> => {
  let status: number;

  try {
    const res: GenericMessageResponse = await cruisesAPI.http.put({
      body: data,
      resource: `${resource}/${id}`,
    });

    status = res.status;
    if (res.result && !res.errors) {
      return res;
    }
  } catch (err) {
    console.error('[Cruise Departure - Update By Id]: ', err);
    return { status, message: err, result: null };
  }
};

/**
 * Find data feed by departure ID
 *
 * @param departureId
 * @param cruiseType - Ocean or River
 * @returns
 */
const getDataFeedByDepartureId = async (
  departureId: string,
  cruiseType: string,
  date?: string,
  options?: { signal?: AbortSignal },
): Promise<CruisesContract.Response<DataFeed>> => {
  let status: number;

  try {
    const dataFeedEndpoint = cruiseType.toUpperCase() === 'RIVER' ? 'river-data-feed' : 'data-feed';

    const res: CruisesContract.Response<DataFeed> = await cruisesAPI.http.get(
      {
        resource: `${resource}/${departureId}/${dataFeedEndpoint}`,
        queryString: date ? JSON.stringify({ date }) : undefined,
      },
      undefined,
      options,
    );

    status = res.status;
    if (res.result && !res.errors) {
      return res;
    }
  } catch (err) {
    console.error('[Data Feed By Departure ID]: ', err);
    return { status, message: err, result: null };
  }
};

const getDataFeedDiffsByDepartureId = async (departureId: string): Promise<CruisesContract.Response<DataFeedDiffs>> => {
  let status: number;

  try {
    const res: CruisesContract.Response<DataFeedDiffs> = await cruisesAPI.http.get({
      resource: `${resource}/${departureId}/data-feed/diffs`,
    });

    status = res.status;
    if (res.result && !res.errors) {
      return res;
    }
  } catch (err) {
    console.error('[Data Feed Diffs By Departure ID]: ', err);
    return { status, message: err, result: null };
  }
};

type RebuildDepartureParams = {
  departureId: string;
  isIsolatedDeparture: boolean;
};

type RebuildDeparture = {
  toOfferId: string;
  fromOfferId: string;
};

type RebuildDepartureResponse = CruisesContract.Response<RebuildDeparture>;

const rebuildDeparture = async (data: RebuildDepartureParams): Promise<RebuildDepartureResponse> => {
  return await cruisesAPI.http.post({
    resource: `${resource}/${data.departureId}/rebuild`,
    body: {
      isIsolatedDeparture: data.isIsolatedDeparture,
    },
  });
};

export default {
  getById,
  updateById,
  rebuildDeparture,
  listWithPagination,
  getDataFeedByDepartureId,
  getDataFeedDiffsByDepartureId,
};
